import React from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Phone, MapPin, Search, ChevronDown, UserPlus } from 'lucide-react';

import { Contact, Organization, OrganizationType, contactDisplayName } from '../../types';
import { AddressAutocompleteInput } from '../../components/AddressAutocompleteInput';
import { UI_Z_CLASS, UI_Z_INDEX } from '../../utils/uiZIndex';
import { OrganizationTypePicker } from './OrganizationTypePicker';
import { useDiscardGuard, useUserEdited } from '../../hooks/useDiscardGuard';
import {
  OrganizationRegistryFields,
  organizationRegistryPayload,
} from './OrganizationRegistryFields';

interface Props {
  onClose: () => void;
  onCreated: (org: Organization) => void;
  /**
   * `drawer` = rechte Seitenleiste wie Plantafel / Neues Projekt.
   * `modal` = zentrierter Dialog (Standard für ältere Aufrufer).
   */
  variant?: 'modal' | 'drawer';
  /** Vorbelegung, z. B. Lieferantenname aus einer Eingangsrechnung. */
  initialName?: string;
  initialType?: OrganizationType;
}

type ApMode = 'none' | 'existing' | 'new';

type ContactOption = {
  id: string | number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone: string;
  address: string;
  company?: string | null;
  organization_id?: string | null;
  contact_kind?: string | null;
  contact_type?: string | null;
};

function isUnattachedGewerblicherContact(c: Contact): boolean {
  if (c.organization_id) return false;
  if (c.contact_kind === 'Gewerblicher Kontakt') return true;
  const t = c.contact_type;
  return t === 'Gewerblicher Kunde' || t === 'Ansprechpartner' || t === 'Lieferant' || t === 'Partner';
}

export const CreateOrganizationModal: React.FC<Props> = ({
  onClose,
  onCreated,
  variant = 'modal',
  initialName = '',
  initialType = 'Gewerblicher Kunde',
}) => {
  const isDrawer = variant === 'drawer';
  const labelClass = isDrawer
    ? 'mb-1 block text-sm font-medium text-app-text-muted'
    : 'mb-1 block text-xs font-medium text-app-text-muted';
  const drawerInputClass =
    'w-full rounded-md border border-app-border bg-app-surface px-3 py-2 text-sm outline-none focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue';
  const [name, setName] = React.useState(initialName);
  const [type, setType] = React.useState<OrganizationType>(initialType);
  const [registeredAddress, setRegisteredAddress] = React.useState('');
  const [vatId, setVatId] = React.useState('');
  const [taxNumber, setTaxNumber] = React.useState('');
  const [commercialRegisterNumber, setCommercialRegisterNumber] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [contacts, setContacts] = React.useState<ContactOption[]>([]);
  const [contactOpen, setContactOpen] = React.useState(false);
  const [contactSearch, setContactSearch] = React.useState('');
  const contactDropdownRef = React.useRef<HTMLDivElement>(null);
  const contactTriggerRootRef = React.useRef<HTMLDivElement>(null);
  const contactMenuPanelRef = React.useRef<HTMLDivElement>(null);
  const contactMenuListRef = React.useRef<HTMLDivElement>(null);
  const [contactMenuPos, setContactMenuPos] = React.useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [apMode, setApMode] = React.useState<ApMode>('none');
  const [contactId, setContactId] = React.useState<string | number | null>(null);
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [address, setAddress] = React.useState('');
  const [contactError, setContactError] = React.useState('');

  React.useEffect(() => {
    fetch('/api/contacts?kind=Gewerblicher Kontakt')
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const raw = Array.isArray(list) ? list : [];
        setContacts(raw.filter((c: Contact) => isUnattachedGewerblicherContact(c)) as ContactOption[]);
      })
      .catch(() => setContacts([]));
  }, []);

  const updateContactMenuPosition = React.useCallback(() => {
    const el = contactTriggerRootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 8;
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const maxPanel = 320;
    let left = rect.left;
    const width = rect.width;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - margin - width);
    }
    if (left < margin) left = margin;
    setContactMenuPos({
      top: rect.bottom + 4,
      left,
      width,
      maxHeight: Math.min(maxPanel, Math.max(0, spaceBelow)),
    });
  }, []);

  React.useLayoutEffect(() => {
    if (!contactOpen) {
      setContactMenuPos(null);
      return;
    }
    updateContactMenuPosition();
    const onScroll = (e: Event) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (contactMenuListRef.current && (contactMenuListRef.current === t || contactMenuListRef.current.contains(t))) {
        return;
      }
      updateContactMenuPosition();
    };
    window.addEventListener('resize', updateContactMenuPosition);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updateContactMenuPosition);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [contactOpen, updateContactMenuPosition]);

  React.useEffect(() => {
    if (!contactOpen) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (contactDropdownRef.current?.contains(t)) return;
      if (contactMenuPanelRef.current?.contains(t)) return;
      setContactOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setContactOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [contactOpen]);

  const selectedContact =
    apMode === 'existing' && contactId != null ? contacts.find((c) => String(c.id) === String(contactId)) : null;

  const apDisplayLabel =
    apMode === 'none'
      ? 'Kein Ansprechpartner'
      : apMode === 'new'
        ? 'Neuen Ansprechpartner anlegen'
        : selectedContact
          ? `${contactDisplayName(selectedContact)}${selectedContact.email ? ` · ${selectedContact.email}` : ''}`.trim() ||
            `Kontakt #${contactId}`
          : 'Ansprechpartner auswählen';

  const filteredContacts = React.useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.first_name || '').toLowerCase().includes(q) ||
        (c.last_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) ||
        (c.company || '').toLowerCase().includes(q),
    );
  }, [contacts, contactSearch]);

  const showApFields = apMode !== 'none';

  const selectNone = () => {
    setContactError('');
    setApMode('none');
    setContactId(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setContactOpen(false);
    setContactSearch('');
  };

  const selectExisting = (c: ContactOption) => {
    setContactError('');
    setApMode('existing');
    setContactId(c.id);
    setFirstName((c.first_name ?? '').trim());
    setLastName((c.last_name ?? '').trim());
    setEmail(c.email || '');
    setPhone(c.phone || '');
    setAddress(c.address || '');
    setContactOpen(false);
    setContactSearch('');
  };

  const selectNew = () => {
    setContactError('');
    setApMode('new');
    setContactId(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setAddress('');
    setContactOpen(false);
    setContactSearch('');
  };

  // Klick daneben, Esc, ✕: nach eigenen Eingaben erst nachfragen.
  const { edited, editProps } = useUserEdited();
  const discardGuard = useDiscardGuard({
    dirty: edited,
    onClose,
    disabled: submitting,
    description: 'Die Organisation ist noch nicht angelegt. Deine Eingaben gehen verloren.',
  });
  const requestClose = discardGuard.requestClose;

  React.useEffect(() => {
    if (!isDrawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (contactOpen) {
        setContactOpen(false);
        return;
      }
      if (
        document.querySelector('[data-app-select-list]') ||
        document.querySelector('[data-app-select-panel]') ||
        document.querySelector('[data-org-type-picker-panel]') ||
        document.querySelector('[data-address-autocomplete-list]')
      ) {
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDrawer, requestClose, contactOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Beide Pflichtangaben auf einmal prüfen, damit nicht nur die erste fehlende gemeldet wird.
    const nameError = !name.trim() ? 'Bitte einen Namen eingeben.' : null;
    const nextContactError =
      apMode === 'new' && !lastName.trim() ? 'Bitte den Nachnamen des Ansprechpartners angeben.' : '';
    setError(nameError);
    setContactError(nextContactError);
    if (nameError || nextContactError) return;

    setSubmitting(true);
    try {
      const res = await fetch('/api/organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          organization_type: type,
          registered_address: registeredAddress.trim() || undefined,
          ...organizationRegistryPayload({ vatId, taxNumber, commercialRegisterNumber }),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? 'Organisation konnte nicht erstellt werden.');
      }
      const created = (await res.json()) as Organization;
      const orgId = String(created.id);
      const orgName = name.trim();

      if (apMode === 'existing' && contactId != null) {
        const patchRes = await fetch(`/api/contacts/${encodeURIComponent(String(contactId))}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_id: orgId,
            company: orgName,
            contact_kind: 'Gewerblicher Kontakt',
            first_name: firstName.trim() || null,
            last_name: lastName.trim() || null,
            email: email.trim() || null,
            phone: phone.trim() || null,
            address: address.trim() || null,
          }),
        });
        if (!patchRes.ok) {
          const body = (await patchRes.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? 'Ansprechpartner konnte nicht zugeordnet werden.');
        }
        const pc = await fetch(`/api/organizations/${orgId}/primary-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact_id: contactId }),
        });
        if (!pc.ok) {
          const body = (await pc.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? 'Hauptansprechpartner konnte nicht gesetzt werden.');
        }
      } else if (apMode === 'new') {
        const createRes = await fetch('/api/contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_kind: 'Gewerblicher Kontakt',
            organization_id: orgId,
            company: orgName,
            first_name: firstName.trim() || undefined,
            last_name: lastName.trim(),
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
            address: address.trim() || undefined,
          }),
        });
        if (!createRes.ok) {
          const body = (await createRes.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? 'Ansprechpartner konnte nicht erstellt werden.');
        }
        const newRow = (await createRes.json()) as Contact;
        const pc = await fetch(`/api/organizations/${orgId}/primary-contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contact_id: newRow.id }),
        });
        if (!pc.ok) {
          const body = (await pc.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? 'Hauptansprechpartner konnte nicht gesetzt werden.');
        }
      }

      const refreshed = await fetch(`/api/organizations/${orgId}`);
      const finalOrg = refreshed.ok ? ((await refreshed.json()) as Organization) : created;
      onCreated(finalOrg);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler.');
    } finally {
      setSubmitting(false);
    }
  };

  const fieldInputClass = isDrawer
    ? drawerInputClass
    : 'w-full px-3 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none text-sm';
  const iconInputClass = isDrawer
    ? drawerInputClass
    : 'w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none text-sm';

  const orgFormFields = (
    <>
      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      <div className={`grid grid-cols-1 ${isDrawer ? 'gap-4' : 'md:grid-cols-2 gap-4 md:gap-6'}`}>
        <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}>
          <label className={labelClass}>Typ</label>
          <OrganizationTypePicker value={type} onChange={setType} variant="default" />
        </div>

        <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}>
          <label className={labelClass}>
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={fieldInputClass}
            placeholder="z. B. Bauunternehmung Becker GmbH"
          />
        </div>

        <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}>
          <label className={labelClass}>
            Firmensitz
            {!isDrawer ? (
              <span className="text-slate-400 font-normal normal-case"> (optional)</span>
            ) : null}
          </label>
          <AddressAutocompleteInput
            value={registeredAddress}
            onChange={setRegisteredAddress}
            inputClassName={fieldInputClass}
            placeholder="Straße, Hausnummer, PLZ Ort"
          />
        </div>

        <OrganizationRegistryFields
          values={{ vatId, taxNumber, commercialRegisterNumber }}
          onChange={(patch) => {
            if (patch.vatId !== undefined) setVatId(patch.vatId);
            if (patch.taxNumber !== undefined) setTaxNumber(patch.taxNumber);
            if (patch.commercialRegisterNumber !== undefined) {
              setCommercialRegisterNumber(patch.commercialRegisterNumber);
            }
          }}
          labelClass={labelClass}
          inputClass={fieldInputClass}
          fieldWrapClass={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}
        />

        <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'} ref={contactDropdownRef}>
          <label className={labelClass}>
            Ansprechpartner
            {!isDrawer ? (
              <span className="text-slate-400 font-normal normal-case"> (optional)</span>
            ) : null}
          </label>
          <div className="relative" ref={contactTriggerRootRef}>
            <button
              type="button"
              aria-expanded={contactOpen}
              aria-haspopup="listbox"
              onClick={() => {
                setContactError('');
                setContactOpen((o) => !o);
              }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-app-text bg-app-surface border outline-none ${
                isDrawer
                  ? 'rounded-md focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue'
                  : 'rounded-lg focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue'
              } ${contactError ? 'border-red-400' : 'border-app-border'}`}
            >
              <span className="truncate">{apDisplayLabel}</span>
              <ChevronDown
                className={`w-4 h-4 shrink-0 text-app-text-subtle transition-transform ${contactOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </div>
          {contactOpen &&
            contactMenuPos &&
            createPortal(
              <div
                ref={contactMenuPanelRef}
                data-org-ap-menu
                role="listbox"
                style={{
                  position: 'fixed',
                  top: contactMenuPos.top,
                  left: contactMenuPos.left,
                  width: contactMenuPos.width,
                  maxHeight: contactMenuPos.maxHeight,
                  zIndex: isDrawer ? UI_Z_INDEX.portalDropdownInModal : 300,
                }}
                className="bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col min-h-0 overscroll-y-contain"
                onWheel={(e) => e.stopPropagation()}
              >
                <div className="p-2 border-b border-app-border shrink-0">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Suchen…"
                      className="w-full pl-8 pr-3 py-1.5 text-sm border border-app-border rounded-md focus:ring-1 focus:ring-pipedrive-blue outline-none"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={selectNone}
                  className={`w-full shrink-0 text-left px-3 py-2.5 text-sm font-medium border-b border-app-border transition-colors ${
                    apMode === 'none' ? 'bg-app-surface-muted text-app-text' : 'text-app-text-muted hover:bg-app-hover'
                  }`}
                >
                  Kein Ansprechpartner
                </button>
                <button
                  type="button"
                  onClick={selectNew}
                  className={`w-full shrink-0 text-left px-3 py-2.5 text-sm font-medium border-b border-app-border flex items-center gap-2 transition-colors ${
                    apMode === 'new'
                      ? 'bg-pipedrive-blue text-white'
                      : 'text-pipedrive-blue hover:bg-sky-50'
                  }`}
                >
                  <UserPlus className="w-4 h-4 shrink-0" />
                  Neuen Ansprechpartner anlegen
                </button>
                <div ref={contactMenuListRef} className="overflow-y-auto flex-1 min-h-0 touch-pan-y">
                  {filteredContacts.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-app-text-subtle">Keine freien Gewerblichen Kontakte.</p>
                  ) : (
                    filteredContacts.map((c) => (
                      <button
                        key={String(c.id)}
                        type="button"
                        role="option"
                        aria-selected={apMode === 'existing' && String(contactId) === String(c.id)}
                        onClick={() => selectExisting(c)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          apMode === 'existing' && String(contactId) === String(c.id)
                            ? 'bg-pipedrive-blue text-white'
                            : 'text-app-text hover:bg-app-hover'
                        }`}
                      >
                        {contactDisplayName(c)}
                        {c.email ? ` · ${c.email}` : ''}
                      </button>
                    ))
                  )}
                </div>
              </div>,
              document.body,
            )}
          {contactError && <p className="text-xs text-red-600 mt-1">{contactError}</p>}
        </div>

        {showApFields && (
          <>
            {apMode === 'existing' && selectedContact && (
              <>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-1'}>
                  <label className={labelClass}>Vorname</label>
                  {isDrawer ? (
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={drawerInputClass}
                      placeholder="Vorname"
                    />
                  ) : (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={iconInputClass}
                        placeholder="Vorname"
                      />
                    </div>
                  )}
                </div>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-1'}>
                  <label className={labelClass}>Nachname</label>
                  {isDrawer ? (
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={drawerInputClass}
                      placeholder="Nachname"
                    />
                  ) : (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={iconInputClass}
                        placeholder="Nachname"
                      />
                    </div>
                  )}
                </div>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}>
                  <label className={labelClass}>Unternehmen</label>
                  <input
                    type="text"
                    readOnly
                    value={orgNamePreview(name)}
                    className={
                      isDrawer
                        ? `${drawerInputClass} bg-app-surface-muted text-app-text-muted`
                        : 'w-full px-4 py-2 bg-app-surface-muted border border-app-border rounded text-sm text-app-text-muted'
                    }
                    title="Wird beim Speichern mit der Organisation verknüpft"
                  />
                </div>
              </>
            )}

            {apMode === 'new' && (
              <>
                {!isDrawer ? (
                  <div className="md:col-span-2">
                    <p className={labelClass}>Ansprechpartner</p>
                  </div>
                ) : null}
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-1'}>
                  <label className={labelClass}>Vorname</label>
                  {isDrawer ? (
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={drawerInputClass}
                      placeholder="z. B. Max"
                    />
                  ) : (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className={iconInputClass}
                        placeholder="z. B. Max"
                      />
                    </div>
                  )}
                </div>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-1'}>
                  <label className={labelClass}>
                    Nachname <span className="text-red-500">*</span>
                  </label>
                  {isDrawer ? (
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={drawerInputClass}
                      placeholder="z. B. Mustermann"
                    />
                  ) : (
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className={iconInputClass}
                        placeholder="z. B. Mustermann"
                      />
                    </div>
                  )}
                </div>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}>
                  <label className={labelClass}>Unternehmen</label>
                  <input
                    type="text"
                    readOnly
                    value={orgNamePreview(name)}
                    className={
                      isDrawer
                        ? `${drawerInputClass} bg-app-surface-muted text-app-text-muted`
                        : 'w-full px-4 py-2 bg-app-surface-muted border border-app-border rounded text-sm text-app-text-muted'
                    }
                  />
                </div>
              </>
            )}

            {(apMode === 'existing' || apMode === 'new') && (
              <>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-1'}>
                  <label className={labelClass}>E-Mail</label>
                  {isDrawer ? (
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={drawerInputClass}
                      placeholder="kunde@beispiel.de"
                    />
                  ) : (
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={iconInputClass}
                        placeholder="kunde@beispiel.de"
                      />
                    </div>
                  )}
                </div>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-1'}>
                  <label className={labelClass}>Telefon</label>
                  {isDrawer ? (
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className={drawerInputClass}
                      placeholder="+49 123 456789"
                    />
                  ) : (
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={iconInputClass}
                        placeholder="+49 123 456789"
                      />
                    </div>
                  )}
                </div>
                <div className={isDrawer ? 'block text-sm' : 'space-y-1 md:col-span-2'}>
                  <label className={labelClass}>Adresse</label>
                  {isDrawer ? (
                    <AddressAutocompleteInput
                      value={address}
                      onChange={setAddress}
                      inputClassName={drawerInputClass}
                      placeholder="Straße, Hausnummer, PLZ Ort"
                    />
                  ) : (
                    <div className="relative">
                      <MapPin className="absolute left-3 top-[14px] z-[1] w-4 h-4 text-slate-400 pointer-events-none" />
                      <AddressAutocompleteInput
                        value={address}
                        onChange={setAddress}
                        inputClassName={iconInputClass}
                        placeholder="Straße, Hausnummer, PLZ Ort"
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );

  return isDrawer ? (
    createPortal(
      <>
        <div
          className={`fixed inset-0 ${UI_Z_CLASS.plantafelDrawer} flex justify-end bg-black/25`}
          role="presentation"
          {...discardGuard.backdropProps}
        >
          <div
            className="relative flex h-full w-[min(400px,100%)] flex-col border-l border-app-border bg-app-surface shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-org-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-app-border/60 p-4">
              <h3 id="create-org-drawer-title" className="truncate text-lg font-semibold text-app-text">
                Neue Organisation anlegen
              </h3>
              <button type="button" className="rounded p-1 text-app-text-subtle hover:bg-app-hover" onClick={requestClose}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <form id="create-org-form" onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" {...editProps}>
              <div className="app-scrollbar flex-1 space-y-4 overflow-y-auto overscroll-contain p-4">
                {orgFormFields}
              </div>
              <div className="flex flex-wrap gap-2 border-t border-app-border/60 p-3">
                <button type="submit" disabled={submitting} className="btn-pipedrive-green flex-1 !py-2 disabled:opacity-50">
                  {submitting ? 'Erstellen …' : 'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
        {discardGuard.dialog}
      </>,
      document.body,
    )
  ) : (
    <>
      <div
        className="fixed inset-0 z-[220] flex flex-col bg-app-surface md:items-center md:justify-center md:bg-black/40 md:p-4 md:backdrop-blur-[2px]"
        {...discardGuard.backdropProps}
      >
        <div
          className="flex flex-col w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-md md:bg-app-surface md:shadow-xl md:overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="md:hidden shrink-0 px-4 py-3 border-b border-app-border flex items-center justify-between bg-app-surface">
            <button type="button" onClick={requestClose} className="p-1.5 -ml-1.5 rounded text-app-text-subtle hover:bg-app-hover">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-base font-semibold text-app-text">Neue Organisation</h2>
            <button
              type="submit"
              form="create-org-form"
              disabled={submitting}
              className="btn-pipedrive-green text-sm disabled:opacity-50"
            >
              {submitting ? '…' : 'Erstellen'}
            </button>
          </div>

          <div className="hidden md:flex px-5 py-3 border-b border-app-border items-center justify-between bg-app-surface-muted/50">
            <h2 className="text-base font-bold text-app-text">Neue Organisation</h2>
            <button type="button" onClick={requestClose} className="p-1 hover:bg-slate-200 rounded">
              <X className="w-4 h-4 text-app-text-subtle" />
            </button>
          </div>

          <form id="create-org-form" onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" {...editProps}>
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
              {orgFormFields}
            </div>

            <div className="hidden md:flex shrink-0 px-6 py-4 border-t border-app-border justify-end gap-3 bg-app-surface-muted/50">
              <button type="button" onClick={onClose} className="btn-pipedrive-secondary">
                Abbrechen
              </button>
              <button type="submit" disabled={submitting} className="btn-pipedrive-green disabled:opacity-50">
                {submitting ? 'Erstellen …' : 'Erstellen'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {discardGuard.dialog}
    </>
  );
};

function orgNamePreview(n: string): string {
  const t = n.trim();
  return t || '— (Name der Organisation)';
}
